import { Controller } from "@hotwired/stimulus"
import { speichereSerienSichtbarkeit, speichereZeitachsenVariante } from "../charts/kredit/charteinstellungen_api.js"
import { createKreditChart } from "../charts/kredit/chart.js"
import { chartXZuReal } from "../charts/kredit/zeitachse.js"
import { renderPhasenlabels } from "../charts/kredit/phasenlabels.js"
import { openNeueSondertilgung } from "../charts/kredit/sondertilgung.js"

function turboVisit(url, options) {
	return globalThis.Turbo?.visit(url, options)
}

async function transitionEnter(element) {
	if (!element) return

	const { enter } = await import("el-transition")
	return enter(element)
}

async function transitionLeave(element) {
	if (!element) return

	const { leave } = await import("el-transition")
	return leave(element)
}

export default class extends Controller {
	static targets = ["data", "phasenlabels", "zoombutton", "chartView", "tableView", "chartButton", "tableButton"]
	static values = {
		kreditId: String,
	}

	initialize() {
		this.chartData = null
		this.activeChart = createKreditChart()
		this.activeVariante = "laufzeit"
		this.activePanel = "chart"
	}

	connect() {
		if (this.hasDataTarget) this.setChartData()
		this.mountChart()
		this.applyViewState()
	}

	dataTargetConnected() {
		this.setChartData()
		this.applyViewState()
		this.activeChart?.update()
	}

	resetZoom() {
		this.activeChart?.resetZoom()
	}

	showZoombutton() {
		if (this.hasZoombuttonTarget) transitionEnter(this.zoombuttonTarget)
	}

	hideZoombutton() {
		if (this.hasZoombuttonTarget) transitionLeave(this.zoombuttonTarget)
	}

	setChartData() {
		const incoming = JSON.parse(this.dataTarget.dataset.chartdata)
		this.chartData = incoming
		this.ensureCharteinstellungen(this.chartData)
		this.activeVariante = this.chartData.charteinstellungen.zeitachsen_variante || "laufzeit"

		this.assignChartData(this.chartData)
	}

	renderPhasenlabels() {
		if (!this.hasPhasenlabelsTarget || !this.activeChart?.chart) return

		renderPhasenlabels({
			target: this.phasenlabelsTarget,
			labels: this.activeChart.visiblePhasenlabels(),
		})
	}

	handleLegendToggle(seriesId, willBeVisible) {
		this.activeChart.toggleHiddenSeriesLocal(seriesId, willBeVisible)

		speichereSerienSichtbarkeit({
			kreditId: this.kreditIdValue,
			seriesId: seriesId,
			visible: willBeVisible,
		}).catch((e) => console.error("Chart-Einstellungen konnten nicht gespeichert werden:", e))
	}

	setZoombuttonSichtbarkeit(visible) {
		if (visible) {
			this.showZoombutton()
		} else {
			this.hideZoombutton()
		}
	}

	showBerechnungLoading() {
		this.activeChart?.showLoading()
	}

	hideBerechnungLoading() {
		this.activeChart?.hideLoading()
	}

	showTable() {
		this.activePanel = "table"
		this.applyViewState()
	}

	showChart() {
		this.activePanel = "chart"
		this.applyViewState()
		this.activeChart?.chart?.reflow()
	}

	selectZeitachsenVariante({ params, detail } = {}) {
		this.activePanel = "chart"
		this.switchVariante(params?.variante || params?.value || detail?.value)
	}

	switchVariante(variante) {
		if (this.activeVariante === variante) {
			this.applyViewState()
			this.activeChart?.chart?.reflow()
			return
		}

		this.setActiveVariante(variante)
		this.refreshChartNachVariantenwechsel()
		speichereZeitachsenVariante({
			kreditId: this.kreditIdValue,
			zeitachsenVariante: variante,
		}).catch((e) => console.error("Chart-Variante konnte nicht gespeichert werden:", e))
	}

	mountChart() {
		if (!this.chartData) return

		this.configureChart(this.activeChart)
		this.assignChartData(this.chartData)
		this.activeChart.init()
	}

	assignChartData(data) {
		if (!this.activeChart || !data) return

		Object.assign(this.activeChart.data, data)
		this.ensureCharteinstellungen(this.activeChart.data)
		this.activeChart.data.chartXZuReal = (utcMs) => chartXZuReal(utcMs, this.activeChart.data)
		this.activeChart.data.onSondertilgungspotentialClick = (payload) =>
			this.activeChart.onSondertilgungspotentialClick?.(payload)
	}

	configureChart(chart) {
		chart.setZoomCallbacks({
			onZoomIn: () => this.setZoombuttonSichtbarkeit(true),
			onZoomReset: () => this.setZoombuttonSichtbarkeit(false),
		})
		chart.setRenderCallback(() => this.renderPhasenlabels())
		chart.setChartSettingsCallbacks({
			onLegendToggle: (seriesId, willBeVisible) => this.handleLegendToggle(seriesId, willBeVisible),
		})
		chart.setInteractionCallbacks({
			onSondertilgungspotentialClick: ({ zeitpunktUtcMs, kreditId }) => {
				openNeueSondertilgung({
					zeitpunktUtcMs,
					kreditstartUtcMs: chart.data.kreditstart,
					kreditId,
					visit: turboVisit,
					frameId: "modal_neu",
				})
			},
		})
	}

	ensureCharteinstellungen(data) {
		data.charteinstellungen ||= { hidden_series: [], zeitachsen_variante: "laufzeit" }
		return data.charteinstellungen
	}

	setActiveVariante(variante) {
		this.activeVariante = variante
		if (!this.chartData) return

		this.ensureCharteinstellungen(this.chartData).zeitachsen_variante = variante
		this.assignChartData(this.chartData)
	}

	refreshChartNachVariantenwechsel() {
		this.setZoombuttonSichtbarkeit(false)
		this.phasenlabelsTarget?.replaceChildren()
		this.activeChart.update()
		this.applyViewState()
		this.activeChart?.chart?.reflow()
	}

	applyViewState() {
		if (this.hasChartViewTarget) this.chartViewTarget.classList.toggle("hidden", this.activePanel !== "chart")
		if (this.hasTableViewTarget) this.tableViewTarget.classList.toggle("hidden", this.activePanel !== "table")

		if (this.hasChartButtonTarget) this.chartButtonTarget.dataset.ui = this.activePanel === "chart" ? "aktiv" : ""
		if (this.hasTableButtonTarget) this.tableButtonTarget.dataset.ui = this.activePanel === "table" ? "aktiv" : ""

		this.dispatch("state-change", {
			detail: {
				activePanel: this.activePanel,
				activeValue: this.activeVariante,
			},
		})
	}
}
